import dayjs from 'dayjs'
import { motion, useIsPresent } from 'framer-motion'
import { CSSProperties, FC, useMemo } from 'react'

import AlertCircleIcon from '@/assets/icons/alert-circle.svg?react'
import CheckCircleIcon from '@/assets/icons/check-circle.svg?react'
import CrossIcon from '@/assets/icons/cross.svg?react'
import CrossCircleIcon from '@/assets/icons/cross-circle.svg?react'
import InfoCircleIcon from '@/assets/icons/info-circle.svg?react'
import { cn } from '@/utils/style'

import { NOTIFICATION_THEMES, NotificationType, NotificationVariant } from './type'

interface NotificationItemProps {
  notification: NotificationType
  onClose: (id: string) => void
  className?: string
  stacked?: boolean
  stackIndex?: number
}

const ICONS: Record<NotificationVariant, FC<React.SVGProps<SVGSVGElement>>> = {
  info: InfoCircleIcon,
  success: CheckCircleIcon,
  warning: AlertCircleIcon,
  error: CrossCircleIcon,
}

function formatTimestamp(timestamp?: Date | string): string {
  return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')
}

export function NotificationItem({
  notification,
  onClose,
  className,
  stacked = false,
  stackIndex = 0,
}: NotificationItemProps) {
  const { variant, title, content, timestamp, closable = true } = notification
  const theme = NOTIFICATION_THEMES[variant]
  const IconComp = ICONS[variant]

  const isPresent = useIsPresent()

  const formattedTimestamp = useMemo(() => formatTimestamp(timestamp), [timestamp])

  const animations = {
    style: {
      position: (isPresent ? 'relative' : 'absolute') as CSSProperties['position'],
      right: isPresent ? 0 : undefined,
      top: isPresent ? 0 : undefined,
      ...(stacked && stackIndex > 0
        ? {
            transform: `translateY(${stackIndex * 8}px) scale(${1 - stackIndex * 0.02})`,
            opacity: 1 - stackIndex * 0.15,
            zIndex: 10 - stackIndex,
          }
        : {}),
    },
    initial: { x: 100, opacity: 0 },
    animate: {
      x: 0,
      opacity: stacked && stackIndex > 0 ? 1 - stackIndex * 0.15 : 1,
    },
    exit: { x: 100, opacity: 0 },
    transition: { type: 'spring', stiffness: 500, damping: 40 },
  }

  return (
    <motion.div {...animations} layout>
      <div
        className={cn(
          'w-[360px] bg-panel-white rounded-2xl p-4 shadow-lg border border-neutral-100',
          'flex items-start gap-3',
          className,
        )}
        style={{
          background: `${theme.gradient}, rgb(var(--color-panel-white))`,
        }}
      >
        {/* Icon */}
        <div className="flex-shrink-0" style={{ color: theme.iconColor }}>
          <IconComp className="w-5 h-5" />
        </div>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="t-sb">{title}</div>

          {/* Content */}
          {content && <div className="t-xs text-secondary mt-2">{content}</div>}

          {/* Timestamp */}
          <div className="text-tertiary t-xs mt-2">{formattedTimestamp}</div>
        </div>

        {/* Close button */}
        {closable && (
          <button
            onClick={() => onClose(notification.id)}
            className="flex-shrink-0 p-1 -m-1 text-tertiary hover:text-secondary transition-colors rounded"
          >
            <CrossIcon className="w-4 h-4 stroke-current" />
          </button>
        )}
      </div>
    </motion.div>
  )
}
